/**
 * Login flow helpers for the Customer Portal SPA.
 *
 * All calls go through the same-origin BFF (`/api/portal/auth/*`). The BFF
 * mints the OIDC Bearer for the upstream API and the API sets the
 * `portal_session` cookie on successful verification.
 *
 * CSRF model (portal/index.ts):
 *   - hook 1 issues a `portal_csrf` cookie when none is present.
 *   - hook 2 rejects unsafe methods missing the `X-CSRF-Token` header that
 *     matches the cookie.
 * So before the FIRST unsafe POST the SPA must perform one safe-method GET
 * to seed the cookie. We use `GET /api/portal/me` (returns 401 when no
 * session, but still seeds the cookie). `portalApi` reads the cookie on
 * each request and echoes it.
 */
import { portalApi, PortalApiError } from './apiClient';

export type AuthSeedOutcome = 'authenticated' | 'unauthenticated' | 'network_error';

interface MeResponse {
  ok: true;
  data: { portal_user_id: number; email: string };
}

/**
 * Hit `/api/portal/me` to (a) seed the portal_csrf cookie and (b) discover
 * whether the user is already authenticated. Safe to call multiple times.
 */
export async function seedCsrfAndProbeSession(signal?: AbortSignal): Promise<AuthSeedOutcome> {
  try {
    await portalApi<MeResponse>('/api/portal/me', { signal });
    return 'authenticated';
  } catch (err) {
    if (err instanceof PortalApiError && err.status === 401) {
      return 'unauthenticated';
    }
    return 'network_error';
  }
}

export interface VerifyLoginLinkResult {
  portal_user_id: number;
  email: string;
  /** ログイン後に遷移する同一オリジンの相対パス (server が sanitise 済、既定 '/')。 */
  redirect: string;
}

export async function verifyLoginLink(
  token: string,
  signal?: AbortSignal,
): Promise<VerifyLoginLinkResult> {
  const res = await portalApi<{ ok: true; data: VerifyLoginLinkResult }>(
    '/api/portal/auth/verify-login-link',
    {
      method: 'POST',
      body: { token },
      signal,
    },
  );
  return res.data;
}

export interface SignupInput {
  email: string;
  tos_version: string;
  tos_accepted: true;
  privacy_version: string;
  privacy_accepted: true;
  locale: 'en' | 'ja' | 'zh' | 'ko';
}

export interface SignupResult {
  ok: boolean;
  /** Constant literal — `'verification_email_sent'` on success. */
  message: string;
}

export async function signup(input: SignupInput, signal?: AbortSignal): Promise<SignupResult> {
  const res = await portalApi<{ ok: boolean; message: string }>('/api/portal/auth/signup', {
    method: 'POST',
    body: input,
    signal,
  });
  return { ok: !!res.ok, message: res.message };
}

/**
 * Extended signup payload for the 4-step UI (22-field intake).
 *
 * `PortalSignupRequestSchema` in `packages/shared/src/portal/signup.ts` is
 * the authoritative contract and accepts exactly this shape: the
 * `'yes' | 'no'` radio answers are coerced to boolean at the schema boundary,
 * and `tos_accepted` / `privacy_accepted` are accepted as literal `true`. The
 * empty-string default is never sent — the call site maps `'' → undefined`.
 *
 * `tos_version` / `privacy_version` default to '1.0' here to match the
 * legacy `signup()` 5-field helper. When env-driven versioning ships the
 * SPA will source these from `import.meta.env` instead of the literal.
 */
export interface SignupExtendedInput {
  // No username — email is the login ID. The BFF derives the stored
  // username from the normalized email server-side.
  email: string;
  password: string;
  confirm_password: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  phone: string;
  mobile?: string;
  company_name?: string;
  country: string;
  street_address: string;
  city: string;
  post_code: string;
  licensed_dealer?: 'yes' | 'no' | '';
  job_title?: string;
  website?: string;
  vehicle_interest?: string;
  imported_before?: 'yes' | 'no' | '';
  additional_info?: string;
  agree_terms: true;
  agree_marketing: boolean;
  tos_version: string;
  tos_accepted: true;
  privacy_version: string;
  privacy_accepted: true;
  locale: 'en' | 'ja' | 'zh' | 'ko';
}

export async function signupExtended(
  input: SignupExtendedInput,
  signal?: AbortSignal,
): Promise<SignupResult> {
  const res = await portalApi<{ ok: boolean; message: string }>('/api/portal/auth/signup', {
    method: 'POST',
    body: input,
    signal,
  });
  return { ok: !!res.ok, message: res.message };
}

/** Trivial email shape check — server still validates. Returns true for
 *  obviously-valid inputs only; the server's zod schema is authoritative.
 */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 3 || trimmed.length > 255) return false;
  if (!trimmed.includes('@')) return false;
  if (/\s/.test(trimmed)) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Email + Password / Google / Microsoft (Phase 1)
// SSOT: docs/specs/PORTAL_AUTH_OAUTH_SSOT.md
// ---------------------------------------------------------------------------

export type AuthProviderName = 'google' | 'microsoft';

/**
 * Start an OAuth flow by navigating the top-level window to the BFF's
 * /oauth/<p>/start endpoint. The BFF mints state/nonce, sets a sealed
 * cookie, then redirects to the provider's authorization endpoint. We do
 * NOT use fetch() here — we need a top-level navigation so the eventual
 * callback redirect (which is also a top-level navigation) carries the
 * state cookie under SameSite=Lax.
 *
 * `returnTo` MUST be a same-origin path. The server sanitises it again
 * server-side (reject absolute URLs, protocol-relative URLs, etc.) so a
 * compromised SPA cannot phish the user.
 *
 * `intent` is sealed into the state cookie server-side. 'login' (default)
 * never provisions a new portal account — an unknown identity + email is
 * bounced back with `?error=member_not_registered`. Only the registration
 * pages pass 'register'.
 */
export function startOAuthSignIn(
  provider: AuthProviderName,
  returnTo?: string,
  intent: 'login' | 'register' = 'login',
): void {
  const params = new URLSearchParams();
  if (returnTo) params.set('return_to', returnTo);
  params.set('intent', intent);
  const qs = params.toString();
  const target = `/api/portal/oauth/${provider}/start${qs ? `?${qs}` : ''}`;
  // Use assign() rather than href = so we don't lose the call site in
  // stack traces and so tests can spy via location.assign.
  window.location.assign(target);
}

export interface PasswordRegisterInput {
  email: string;
  password: string;
  display_name?: string;
  locale?: 'en' | 'ja' | 'zh' | 'ko';
}

export interface PasswordLoginInput {
  email: string;
  password: string;
}

export interface PasswordAuthResult {
  ok: boolean;
}

/** Generic catch-all error so the SPA can render a single message. */
export class PasswordAuthError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'PasswordAuthError';
  }
}

export async function registerWithPassword(
  input: PasswordRegisterInput,
  signal?: AbortSignal,
): Promise<PasswordAuthResult> {
  try {
    const res = await portalApi<{ ok: boolean }>('/api/portal/auth/password/register', {
      method: 'POST',
      body: input,
      signal,
    });
    return { ok: !!res.ok };
  } catch (err) {
    if (err instanceof PortalApiError) {
      throw new PasswordAuthError(err.code || `HTTP_${err.status}`);
    }
    throw new PasswordAuthError('NETWORK_ERROR');
  }
}

export async function loginWithPassword(
  input: PasswordLoginInput,
  signal?: AbortSignal,
): Promise<PasswordAuthResult> {
  try {
    const res = await portalApi<{ ok: boolean }>('/api/portal/auth/password/login', {
      method: 'POST',
      body: input,
      signal,
    });
    return { ok: !!res.ok };
  } catch (err) {
    if (err instanceof PortalApiError) {
      // /password/login always returns 401 INVALID_CREDENTIALS for any
      // wrong combination. Surface that as a typed error so the SPA can
      // render the same generic message.
      throw new PasswordAuthError('INVALID_CREDENTIALS');
    }
    throw new PasswordAuthError('NETWORK_ERROR');
  }
}
